import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { HelpCircle, Send, CheckCircle, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QAPage() {
  const [qaItems, setQaItems] = useState([]);
  const [formData, setFormData] = useState({
    question: '',
    asker_name: '',
    asker_email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetchQA();
  }, []);

  const fetchQA = async () => {
    try {
      const response = await axios.get(`${API}/qa?public_only=true`);
      setQaItems(response.data);
    } catch (error) {
      console.error('Error fetching Q&A:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/qa`, formData);
      setSubmitted(true);
      toast.success('Question submitted! We\'ll answer it soon.');
    } catch (error) {
      toast.error('Failed to submit question');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Placeholder Q&A if none exist
  const displayQA = qaItems.length > 0 ? qaItems : [
    {
      id: '1',
      question: 'What are your service times?',
      answer: 'We gather for Sunday Worship at 10:00 AM and Wednesday Bible Study at 7:00 PM. Our Food Pantry operates on Saturdays from 9:00 AM to 12:00 PM.',
      asker_name: 'Community Member'
    },
    {
      id: '2',
      question: 'How can I volunteer at the food pantry?',
      answer: 'We\'d love to have you! Visit our Volunteer page to fill out an interest form, or simply show up on Saturday mornings. No experience needed - just a willing heart!',
      asker_name: 'Maria'
    },
    {
      id: '3',
      question: 'Do I need to be a member to attend services?',
      answer: 'Absolutely not! Everyone is welcome at Living Fruit Ministry. We believe in open doors and open hearts. Come as you are!',
      asker_name: 'First-time Visitor'
    }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-cream pt-24 flex items-center justify-center" data-testid="qa-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto px-6"
        >
          <div className="w-20 h-20 bg-olive rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-white" />
          </div>
          <h1 className="font-serif text-3xl text-charcoal mb-4">Question Received!</h1>
          <p className="text-charcoal/70 mb-8">
            Thank you for your question. Our team will review it and post an answer soon.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setFormData({ question: '', asker_name: '', asker_email: '' });
            }}
            className="bg-burgundy hover:bg-purple text-white"
          >
            Ask Another Question
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-24" data-testid="qa-page">
      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 bg-olive rounded-full flex items-center justify-center mx-auto mb-6">
              <HelpCircle size={32} className="text-white" />
            </div>
            <span className="text-xs uppercase tracking-[0.2em] text-gold mb-4 block">Ask Us Anything</span>
            <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-4">Questions & Answers</h1>
            <p className="text-charcoal/70 max-w-2xl mx-auto">
              Have a question about our ministry, services, or beliefs? Ask here and we'll answer publicly to help others too.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Question Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-1"
            >
              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-3xl p-6 md:p-8 shadow-lg border border-gold/10 sticky top-28"
                data-testid="qa-form"
              >
                <h2 className="font-serif text-xl text-charcoal mb-4">Ask a Question</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label>Your Question</Label>
                    <Textarea
                      value={formData.question}
                      onChange={(e) => setFormData({...formData, question: e.target.value})}
                      required
                      rows={4}
                      placeholder="What would you like to know?"
                      className="bg-cream border-gold/20 focus:border-gold"
                      data-testid="qa-question"
                    />
                  </div>
                  
                  <div>
                    <Label>Your Name (Optional)</Label>
                    <Input
                      value={formData.asker_name}
                      onChange={(e) => setFormData({...formData, asker_name: e.target.value})}
                      placeholder="Anonymous"
                      className="bg-cream border-gold/20 focus:border-gold"
                      data-testid="qa-name"
                    />
                  </div>
                  
                  <div>
                    <Label>Email (Optional - for notification)</Label>
                    <Input
                      type="email"
                      value={formData.asker_email}
                      onChange={(e) => setFormData({...formData, asker_email: e.target.value})}
                      className="bg-cream border-gold/20 focus:border-gold"
                      data-testid="qa-email"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-burgundy hover:bg-purple text-white"
                    data-testid="submit-qa"
                  >
                    {isSubmitting ? 'Submitting...' : (
                      <>
                        <Send size={18} className="mr-2" /> Submit Question
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>

            {/* Q&A List */}
            <div className="lg:col-span-2">
              <h2 className="font-serif text-2xl text-charcoal mb-6">Answered Questions</h2>
              
              <div className="space-y-6">
                {displayQA.map((item, i) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                    className="bg-white rounded-2xl p-6 border border-gold/10"
                    data-testid={`qa-item-${i}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className="bg-gold/10 p-2 rounded-lg">
                        <HelpCircle size={20} className="text-gold" />
                      </div>
                      <div>
                        <h3 className="font-medium text-charcoal">{item.question}</h3>
                        <p className="text-xs text-charcoal/50 mt-1">Asked by {item.asker_name || 'Anonymous'}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 pl-11">
                      <div className="bg-burgundy/10 p-2 rounded-lg">
                        <MessageSquare size={20} className="text-burgundy" />
                      </div>
                      <div>
                        <p className="text-charcoal/80">{item.answer}</p>
                        <p className="text-xs text-burgundy mt-2">— Living Fruit Ministry</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {displayQA.length === 0 && (
                <div className="bg-white rounded-2xl p-8 border border-gold/10 text-center">
                  <HelpCircle size={32} className="text-charcoal/20 mx-auto mb-3" />
                  <p className="text-charcoal/50">No questions answered yet. Be the first to ask!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
